import useModalConfirm from './useModalConfirm'
import { SEARCH_QUERY_DEFAULT, SEARCH_QUERY_URL } from 'constants/query'
import { useOnlineStatus } from 'detectConnectServer'
import { ICrudUrl, ISearchQuery } from 'interfaces'
import { IResponse } from 'interfaces/services'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchQueryService } from 'services/useSearchQueryService'
import {
  querySearchSelector,
  setForceQueryAction,
  setSearchQueryAction
} from 'store/searchQuerySlice'

const useQuerySearch = <T,>(
  searchQueryUrl: ICrudUrl,
  queryDefault?: ISearchQuery<any>,
  searchWhenInit?: boolean
) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const { openConfirmDelete } = useModalConfirm()
  const [totalRecord, setTotalRecord] = useState(0)
  const [totalRecordFilter, setTotalRecordFilter] = useState(0)
  const [items, setItems] = useState([] as Array<T>)
  const [currentPage, setCurrentPage] = useState(1)
  const [data, setData] = useState<any>(null)
  const { search, deleteItem, postDeleteItem, deleteItemWithPayload } =
    useSearchQueryService<T>()
  const payloadSearch = useSelector(querySearchSelector)[searchQueryUrl.name]
  const payloadSearchRef = useRef<ISearchQuery<any>>(payloadSearch)
  const previousLimit = useRef<any>(null)
  const itemsRef = useRef<any>(null)
  const totalItemsRef = useRef<number>(0)
  const totalItemsFilterRef = useRef<number>(0)
  const isOnline = useOnlineStatus()

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    payloadSearchRef.current = payloadSearch
  }, [payloadSearch])

  useEffect(() => {
    totalItemsRef.current = totalRecord
  }, [totalRecord])

  useEffect(() => {
    totalItemsFilterRef.current = totalRecordFilter
  }, [totalRecord])
  useEffect(() => {
    if (payloadSearch?.limit && previousLimit.current) {
      handleGetItems(payloadSearch)
    }
    previousLimit.current = payloadSearch?.limit
  }, [payloadSearch?.limit, payloadSearch?.offset])

  useEffect(() => {
    if (searchWhenInit) {
      if (searchQueryUrl?.name === SEARCH_QUERY_URL?.RepoInventory?.name) {
        handleOnSearchReplaceQuery({
          q: ''
        })
      } else {
        if (payloadSearch) {
          handleGetItems(payloadSearch)
          previousLimit.current = payloadSearch?.limit
        } else {
          if (queryDefault) {
            handleGetItems(queryDefault)
            previousLimit.current = queryDefault?.limit
          }
        }
        dispatch(
          setSearchQueryAction({
            field: searchQueryUrl.name,
            params: payloadSearch || queryDefault
          })
        )
      }
    }
  }, [searchWhenInit, isOnline])

  const handleGetItems = (
    payload: ISearchQuery<any>,
    disabledLoading?: boolean
  ) => {
    setCurrentPage((payload?.offset || 0) / (payload?.limit || 1) + 1)
    if (!disabledLoading) {
      setLoading(true)
    }
    search(searchQueryUrl.search, payload, searchQueryUrl.fieldData)
      .then((res: IResponse<any>) => {
        if (res._successfully) {
          setItems(res?.data?.data)
          setTotalRecord(res?.data?.totalItems ?? totalRecord)
          setTotalRecordFilter(res?.data?.totalItems || 0)
          setData(res.data?.res)
        }
      })
      .finally(() => setLoading(false))
  }

  const handleOnSearchReplaceQuery = (query?: any) => {
    const buildQuery = {
      ...SEARCH_QUERY_DEFAULT,
      ...payloadSearch,
      query: query
    }
    handleGetItems(buildQuery)
    dispatch(
      setSearchQueryAction({
        field: searchQueryUrl.name,
        params: buildQuery
      })
    )
  }

  const handleOnSearchQuery = (query?: any) => {
    const buildQuery = {
      ...payloadSearchRef.current,
      // ...SEARCH_QUERY_DEFAULT,
      query: { ...payloadSearch?.query, ...query }
    }
    handleGetItems(buildQuery)
    dispatch(
      setSearchQueryAction({
        field: searchQueryUrl.name,
        params: buildQuery
      })
    )
  }

  const handleOnSearch = (params?: any) => {
    handleGetItems(params)
    dispatch(
      setForceQueryAction({
        field: searchQueryUrl.name,
        params: params
      })
    )
  }

  const handleOnSearchQueryOutSide = (params: any) => {
    const buildQuery = {
      ...SEARCH_QUERY_DEFAULT,
      ...queryDefault,
      ...payloadSearch,
      ...params
    }
    handleGetItems(buildQuery)
    dispatch(
      setSearchQueryAction({
        field: searchQueryUrl.name,
        params: buildQuery
      })
    )
  }

  const handleOnSearchQueryOnly = (params?: any) => {
    handleGetItems(params)
    dispatch(
      setSearchQueryAction({
        field: searchQueryUrl.name,
        params: params
      })
    )
  }

  const handleDelete = (id: number | string, message: string) => {
    const okDelete = async () => {
      const res = await deleteItem(`${searchQueryUrl.delete}/${id}`)
      reload()
      return res
    }
    openConfirmDelete(message, okDelete)
  }
  const handleDeleteImport = (id: number | string, message: string) => {
    const okDelete = async () => {
      const res = await deleteItem(`${searchQueryUrl.delete}/${id}/delete`)
      reload()
      return res
    }
    openConfirmDelete(message, okDelete)
  }
  const handleDeleteWithPayload = (query: any, message: string) => {
    const okDelete = async () => {
      const res = await deleteItemWithPayload(`${searchQueryUrl.delete}`, query)
      reload()
      return res
    }
    openConfirmDelete(message, okDelete)
  }

  const handlePostDelete = (
    id: number | string,
    message: string,
    query: any
  ) => {
    const okDelete = async () => {
      const res = await postDeleteItem(`${searchQueryUrl.delete}`, query)
      reload()
      return res
    }
    openConfirmDelete(message, okDelete)
  }

  const reload = (disabledLoading?: boolean) => {
    handleGetItems(payloadSearchRef.current, disabledLoading)
  }

  const getPayloadSearch = () => {
    return payloadSearchRef.current
  }

  const getTotalRecord = () => {
    return totalItemsRef.current
  }
  const getTotalRecordFilter = () => {
    return totalItemsFilterRef.current
  }
  const getItems = () => {
    return itemsRef.current
  }
  return {
    items,
    data,
    loading,
    totalRecord,
    totalRecordFilter,
    payloadSearch,
    currentPage,
    offset: payloadSearch?.offset || 0,
    reload,
    handleDelete,
    handleOnSearch,
    handlePostDelete,
    handleOnSearchQuery,
    handleOnSearchQueryOutSide,
    handleDeleteWithPayload,
    handleOnSearchQueryOnly,
    handleOnSearchReplaceQuery,
    handleDeleteImport,
    getPayloadSearch,
    getItems,
    setItems,
    setTotalRecord,
    getTotalRecordFilter,
    getTotalRecord
  }
}

export default useQuerySearch
